import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, XCircle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogDescription,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'

interface CancelSubscriptionModalProps {
    isOpen: boolean
    onOpenChange: (open: boolean) => void
    onConfirm: (cancelAtPeriodEnd: boolean) => void
    isPending: boolean
}

export const CancelSubscriptionModal = ({
    isOpen,
    onOpenChange,
    onConfirm,
    isPending,
}: CancelSubscriptionModalProps) => {
    const { t } = useTranslation('subscriptions')
    const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true)

    const handleConfirm = () => {
        onConfirm(cancelAtPeriodEnd)
    }

    const handleOpenChange = (open: boolean) => {
        if (!open && !isPending) {
            onOpenChange(false)
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-[450px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <XCircle className="h-5 w-5 text-destructive" />
                        {t('cancelModal.title')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('cancelModal.description')}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-1">
                            <Label htmlFor="cancel-at-period-end" className="font-medium">
                                {t('cancelModal.cancelAtPeriodEnd')}
                            </Label>
                            <p className="text-sm text-muted-foreground">
                                {cancelAtPeriodEnd
                                    ? t('cancelModal.periodEndDescription')
                                    : t('cancelModal.immediateDescription')}
                            </p>
                        </div>
                        <Switch
                            id="cancel-at-period-end"
                            checked={cancelAtPeriodEnd}
                            onCheckedChange={setCancelAtPeriodEnd}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        onClick={() => onOpenChange(false)}
                        variant="outline"
                        disabled={isPending}
                    >
                        {t('cancelModal.keepSubscription')}
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        variant="destructive"
                        disabled={isPending}
                    >
                        {isPending && <Loader2 className="size-4 animate-spin mr-2" />}
                        {t('cancelModal.confirm')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

